import { useState, useEffect } from "react";
import { openPaywall } from "../components/Paywall";
import { NotificationBell } from "../components/RealtimeNotifier";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence, useMotionValue, useTransform } from "framer-motion";
import { useAuth } from "../App";
import BottomNav from "../components/BottomNav";
import MatchModal from "../components/MatchModal";
import FiltersModal from "../components/FiltersModal";
import AdvancedFiltersModal from "../components/AdvancedFiltersModal";
import AISearchFilter from "../components/AISearchFilter";
import SuperLikeModal from "../components/SuperLikeModal";
import { SkeletonCard } from "../components/SkeletonCard";
import EmptyState from "../components/EmptyState";
import Onboarding from "../components/Onboarding";
import { usePromoPopup, trackSwipe, trackMatch } from "../components/PromoPopup";
import PhotoRequiredModal, { usePhotoRequired } from "../components/PhotoRequiredModal";
import ReviewPromptModal from "../components/ReviewPromptModal";
import { useReviewPrompt } from "../hooks/useReviewPrompt";
import BubbleView from "../components/BubbleView";
import { Heart, X, Star, RotateCcw, MapPin, SlidersHorizontal, BadgeCheck, MessageCircle, Sparkles, Zap, Circle } from "lucide-react";
import { Button } from "../components/ui/button";
import { useLanguage } from "../contexts/LanguageContext";

import { API } from "../config";
import { tx } from "../lib/tx";

// Key for storing filters in localStorage
const FILTERS_STORAGE_KEY = "discover_filters";

// Load saved filters from localStorage
const loadSavedFilters = () => {
  try {
    const saved = localStorage.getItem(FILTERS_STORAGE_KEY);
    if (saved) {
      return JSON.parse(saved);
    }
  } catch (e) {
    console.error("Error loading saved filters:", e);
  }
  return null;
};

// Get default filters based on user preferences
const getDefaultFiltersFromUser = (user) => {
  const defaults = {
    min_age: 18,
    max_age: 65,
    max_distance: 100,
    gender: "",
    department: "",
    search_lat: null,
    search_lon: null,
    search_city: null
  };
  
  if (!user) return defaults;
  
  // Apply user's interested_in preference to gender filter
  if (user.interested_in) {
    if (user.interested_in === "men") {
      defaults.gender = "Homme";
    } else if (user.interested_in === "women") {
      defaults.gender = "Femme";
    }
    // "both" = no gender filter (empty string)
  }
  
  return defaults;
};

export default function Discover() {
  const navigate = useNavigate();
  const { user, setUser } = useAuth();
  const { language } = useLanguage();
  const [profiles, setProfiles] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showMatch, setShowMatch] = useState(false);
  const [matchedUser, setMatchedUser] = useState(null);
  const [history, setHistory] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showSuperLikeModal, setShowSuperLikeModal] = useState(false);
  const [superLikeTarget, setSuperLikeTarget] = useState(null);
  const [messageStatus, setMessageStatus] = useState({ free_messages_remaining: 3 });
  const [isAnimating, setIsAnimating] = useState(false);
  const [showPhotoRequired, setShowPhotoRequired] = useState(false);
  const [showBubbleView, setShowBubbleView] = useState(false);
  
  // Promo popup hook
  const { showPromo, hidePromo } = usePromoPopup();
  useEffect(() => {
    if (showPromo) { hidePromo(); openPaywall("promo"); }
  }, [showPromo, hidePromo]);
  
  // Review prompt hook
  const { 
    showReviewPrompt, 
    triggerSource, 
    triggerAfterMatch, 
    triggerAfterUsageDuration,
    handleReviewResponse, 
    closePrompt: closeReviewPrompt 
  } = useReviewPrompt(user);
  
  // Check if user needs photo
  const needsPhoto = usePhotoRequired(user);
  
  // Get user-based default filters
  const userDefaultFilters = getDefaultFiltersFromUser(user);
  
  // Load filters from localStorage or use user preferences
  const [filters, setFilters] = useState(() => {
    const saved = loadSavedFilters();
    // If no saved filters, use user preferences
    if (!saved) {
      return userDefaultFilters;
    }
    return saved;
  });
  
  // Force refresh counter to trigger reload even with same filters
  const [refreshCounter, setRefreshCounter] = useState(0);

  // Save filters to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(filters));
  }, [filters]);

  // Check for usage-based review trigger on mount
  useEffect(() => {
    // Small delay to ensure user data is loaded
    const timer = setTimeout(() => {
      triggerAfterUsageDuration();
    }, 3000);
    return () => clearTimeout(timer);
  }, [triggerAfterUsageDuration]);

  useEffect(() => {
    fetchMessageStatus();
  }, []);

  useEffect(() => {
    fetchProfiles();
  }, [filters, refreshCounter]);

  const fetchMessageStatus = async () => {
    try {
      const token = localStorage.getItem("session_token");
      const response = await fetch(`${API}/messages/status/daily`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setMessageStatus(data);
      }
    } catch (error) {
      console.error("Message status error:", error);
    }
  };

  const fetchProfiles = async () => {
    try {
      const token = localStorage.getItem("session_token");
      const params = new URLSearchParams({
        min_age: filters.min_age.toString(),
        max_age: filters.max_age.toString(),
        max_distance: filters.max_distance.toString()
      });
      if (filters.gender) params.append("gender", filters.gender);
      if (filters.department) params.append("department", filters.department);
      
      // Add search coordinates from AI search
      if (filters.search_lat && filters.search_lon) {
        params.append("search_lat", filters.search_lat.toString());
        params.append("search_lon", filters.search_lon.toString());
      }

      const response = await fetch(`${API}/users/discover?${params}`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setProfiles(data);
        setCurrentIndex(0);
      }
    } catch (error) {
      console.error("Fetch error:", error);
      alert(tx("Erreur de chargement des profils"));
    } finally {
      setLoading(false);
    }
  };

  const handleFavorite = async (profile) => {
    try {
      const token = localStorage.getItem("session_token");
      const response = await fetch(`${API}/favorites/${profile.user_id}`, {
        method: "POST",
        headers: { "Authorization": `Bearer ${token}` }
      });
      
      if (response.ok) {
        alert(tx("Ajouté aux favoris ⭐"));
      }
    } catch (error) {
      console.error("Favorite error:", error);
    }
  };

  const handleSuperLike = async (credits) => {
    if (!superLikeTarget) return;
    
    try {
      const token = localStorage.getItem("session_token");
      const response = await fetch(`${API}/super-like/${superLikeTarget.user_id}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({ credits_used: credits })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setUser(prev => ({ ...prev, credits: data.credits_remaining }));
        
        if (data.is_match) {
          setMatchedUser(superLikeTarget);
          setShowMatch(true);
        } else {
          alert(tx("Super Like envoyé ! 💫"));
        }
        
        setHistory(prev => [...prev, { index: currentIndex, profile: superLikeTarget }]);
        setCurrentIndex(prev => prev + 1);
      } else if (response.status !== 402) {
        alert(data.detail || tx("Erreur"));
      }
    } catch (error) {
      console.error("Super like error:", error);
      alert(tx("Erreur lors de l'envoi du Super Like"));
    } finally {
      setShowSuperLikeModal(false);
      setSuperLikeTarget(null);
    }
  };

  const handleAction = async (action) => {
    if (currentIndex >= profiles.length || isAnimating) return;
    
    const targetUser = profiles[currentIndex];
    
    // Check if user needs photo before allowing likes
    if ((action === "like" || action === "super_like") && needsPhoto) {
      setShowPhotoRequired(true);
      return;
    }
    
    // Handle super_like differently - show modal
    if (action === "super_like") {
      setSuperLikeTarget(targetUser);
      setShowSuperLikeModal(true);
      return;
    }

    // Lock during animation
    setIsAnimating(true);
    
    // Track swipe for promo popup
    trackSwipe();

    try {
      const token = localStorage.getItem("session_token");
      const response = await fetch(`${API}/likes`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({
          target_user_id: targetUser.user_id,
          action_type: action
        })
      });

      const data = await response.json();

      if (data.is_match) {
        setMatchedUser(targetUser);
        setShowMatch(true);
        // Track match for promo popup
        trackMatch();
      }

      setHistory(prev => [...prev, { index: currentIndex, profile: targetUser }]);
      
      // Small delay before changing index to let animation complete smoothly
      setTimeout(() => {
        setCurrentIndex(prev => prev + 1);
        setIsAnimating(false);
      }, 150);

    } catch (error) {
      console.error("Action error:", error);
      alert(tx("Erreur lors de l'action"));
      setIsAnimating(false);
    }
  };

  const handleSendMessage = (targetUser) => {
    navigate(`/chat/${targetUser.user_id}`);
  };

  const handleViewProfile = (targetUser) => {
    navigate(`/user/${targetUser.user_id}`);
  };

  const handleUndo = () => {
    if (history.length === 0) return;
    const lastAction = history[history.length - 1];
    setHistory(prev => prev.slice(0, -1));
    setCurrentIndex(lastAction.index);
  };

  const currentProfile = profiles[currentIndex];

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        {/* Show onboarding even during loading for new users */}
        <Onboarding language={language} onComplete={() => {}} />
        
        <header className="p-4 flex items-center justify-between">
          <div className="h-8 w-32 bg-muted/50 rounded-lg animate-pulse" />
          <div className="h-10 w-20 bg-muted/50 rounded-full animate-pulse" />
        </header>
        <div className="flex-1 px-4 pb-24">
          <SkeletonCard />
        </div>
        <BottomNav />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="discover-page">
      {/* Onboarding for new users */}
      <Onboarding language={language} onComplete={() => setShowOnboarding(false)} />
      
      <div className="pt-3 px-3" style={{ paddingBottom: '4.5rem' }}>
        <div className="max-w-md mx-auto">
          {/* Filters Buttons - All in one row */}
          <div className="flex justify-center gap-2 mb-2 flex-wrap">
            <NotificationBell />
            <AISearchFilter 
              language={language}
              compact={true}
              onFiltersGenerated={(aiFilters) => {
                const newFilters = {
                  ...filters,
                  min_age: aiFilters.min_age || filters.min_age,
                  max_age: aiFilters.max_age || filters.max_age,
                  max_distance: aiFilters.max_distance || filters.max_distance,
                  gender: aiFilters.gender === 'female' ? 'Femme' : aiFilters.gender === 'male' ? 'Homme' : filters.gender,
                  // Add city coordinates from AI search
                  search_lat: aiFilters.city_lat || null,
                  search_lon: aiFilters.city_lon || null,
                  search_city: aiFilters.city || null
                };
                setFilters(newFilters);
                // fetchProfiles will be called automatically via useEffect
              }}
            />
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowAdvancedFilters(true)}
              className="rounded-full border-primary/50 text-primary bg-primary/10 hover:bg-primary/20"
              data-testid="advanced-filters-btn"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              {language === "en" ? "Advanced" : "Avancés"}
            </Button>
            {/* Bouton Mode Bulle */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowBubbleView(true)}
              className="rounded-full border-blue-500/50 text-blue-400 bg-blue-500/10 hover:bg-blue-500/20"
              data-testid="bubble-view-btn"
            >
              <Circle className="w-4 h-4 mr-2" />
              {language === "en" ? "Bubbles" : "Bulles"}
            </Button>
          </div>

          {/* Card Stack - Responsive height */}
          <div className="relative w-full card-height-responsive">
            <AnimatePresence>
              {profiles.slice(currentIndex, currentIndex + 3).reverse().map((profile, i) => (
                <SwipeCard
                  key={profile.user_id}
                  profile={profile}
                  isTop={i === profiles.slice(currentIndex, currentIndex + 3).length - 1}
                  onSwipe={handleAction}
                  onMessage={() => handleSendMessage(profile)}
                  onViewProfile={handleViewProfile}
                  onFavorite={handleFavorite}
                  onSuperLike={() => {
                    setSuperLikeTarget(profile);
                    setShowSuperLikeModal(true);
                  }}
                  messageStatus={messageStatus}
                />
              ))}
            </AnimatePresence>

            {currentIndex >= profiles.length && (
              <EmptyState 
                type="noProfiles" 
                language={language}
                action={() => {
                  // Reset filters to user default preferences
                  const defaultFilters = getDefaultFiltersFromUser(user);
                  setFilters(defaultFilters);
                  // Reset current index to 0
                  setCurrentIndex(0);
                  // Clear localStorage to ensure fresh filters
                  localStorage.removeItem(FILTERS_STORAGE_KEY);
                  // Force refresh by incrementing counter
                  setRefreshCounter(prev => prev + 1);
                  setLoading(true);
                }}
                actionLabel={language === 'fr' ? 'Réinitialiser les filtres' : 'Reset filters'}
              />
            )}
          </div>

          {/* Action Buttons - Responsive */}
          {currentProfile && (
            <div className="flex justify-center items-center gap-2 sm:gap-3 mt-2 px-2">
              <button
                onClick={handleUndo}
                disabled={history.length === 0}
                className="action-btn-sm rounded-full bg-card border border-white/10 flex items-center justify-center text-muted-foreground hover:text-white hover:border-white/30 transition-all disabled:opacity-30 touch-target"
                data-testid="undo-btn"
              >
                <RotateCcw className="w-4 h-4 sm:w-5 sm:h-5" />
              </button>
              
              <button
                onClick={() => handleAction("pass")}
                className="action-btn-md rounded-full bg-card border border-red-500/50 flex items-center justify-center text-red-500 hover:bg-red-500/20 hover:border-red-500 transition-all touch-target"
                data-testid="pass-btn"
              >
                <X className="w-6 h-6 sm:w-7 sm:h-7" />
              </button>

              {/* Message Button */}
              <button
                onClick={() => handleSendMessage(currentProfile)}
                className="action-btn-sm rounded-full bg-blue-500/20 border border-blue-500/50 flex flex-col items-center justify-center text-blue-500 hover:bg-blue-500/30 hover:border-blue-500 transition-all touch-target"
                data-testid="message-btn"
              >
                <MessageCircle className="w-4 h-4 sm:w-5 sm:h-5" />
                <span className="text-[9px] sm:text-[10px] font-bold">{messageStatus.is_premium ? "∞" : messageStatus.free_messages_remaining}</span>
              </button>
              
              <button
                onClick={() => handleAction("super_like")}
                className="action-btn-sm rounded-full gradient-gold flex items-center justify-center text-black shadow-lg hover:scale-110 transition-all touch-target"
                data-testid="super-like-btn"
              >
                <Star className="w-5 h-5 sm:w-6 sm:h-6 fill-current" />
              </button>
              
              <button
                onClick={() => handleAction("like")}
                className="action-btn-md rounded-full bg-primary flex items-center justify-center text-white shadow-lg glow-primary hover:scale-110 transition-all touch-target"
                data-testid="like-btn"
              >
                <Heart className="w-6 h-6 sm:w-7 sm:h-7 fill-current" />
              </button>
            </div>
          )}
        </div>
      </div>

      <BottomNav />
      
      <MatchModal 
        isOpen={showMatch} 
        onClose={() => setShowMatch(false)} 
        matchedUser={matchedUser}
        currentUser={user}
        onMatchCelebrated={triggerAfterMatch}
      />

      <FiltersModal
        isOpen={showFilters}
        onClose={() => setShowFilters(false)}
        filters={filters}
        onApply={(newFilters) => {
          setFilters(newFilters);
          setShowFilters(false);
        }}
      />

      <AdvancedFiltersModal
        isOpen={showAdvancedFilters}
        onClose={() => setShowAdvancedFilters(false)}
        baseFilters={filters}
        onApply={(advancedFilters) => {
          // Merge advanced filters with base filters
          setFilters(prev => ({
            ...prev,
            min_age: advancedFilters.min_age,
            max_age: advancedFilters.max_age,
            max_distance: advancedFilters.max_distance,
            gender: advancedFilters.gender || prev.gender
          }));
          setShowAdvancedFilters(false);
        }}
      />

      <SuperLikeModal
        isOpen={showSuperLikeModal}
        onClose={() => {
          setShowSuperLikeModal(false);
          setSuperLikeTarget(null);
        }}
        onConfirm={handleSuperLike}
        targetUser={superLikeTarget}
        userCredits={user?.credits || 0}
      />

      {/* Photo Required Modal */}
      {showPhotoRequired && (
        <PhotoRequiredModal
          onClose={() => setShowPhotoRequired(false)}
          language={language}
        />
      )}


      {/* Review Prompt Modal */}
      <ReviewPromptModal
        isOpen={showReviewPrompt}
        onClose={closeReviewPrompt}
        onReviewGiven={handleReviewResponse}
        language={language}
        triggerSource={triggerSource}
      />

      {/* Bubble View Mode */}
      {showBubbleView && (
        <BubbleView
          profiles={profiles}
          onLike={async (userId) => {
            const profile = profiles.find(p => p.user_id === userId);
            if (profile) {
              await handleAction("like");
            }
          }}
          onDislike={async (userId) => {
            const profile = profiles.find(p => p.user_id === userId);
            if (profile) {
              await handleAction("pass");
            }
          }}
          onSuperLike={async (userId) => {
            const profile = profiles.find(p => p.user_id === userId);
            if (profile) {
              setSuperLikeTarget(profile);
              setShowSuperLikeModal(true);
            }
          }}
          onClose={() => setShowBubbleView(false)}
        />
      )}
    </div>
  );
}

function SwipeCard({ profile, isTop, onSwipe, onMessage, messageStatus, onViewProfile, onFavorite, onSuperLike }) {
  const { language } = useLanguage();
  const x = useMotionValue(0);
  const rotate = useTransform(x, [-200, 200], [-25, 25]);
  const likeOpacity = useTransform(x, [0, 100], [0, 1]);
  const nopeOpacity = useTransform(x, [-100, 0], [1, 0]);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  const handleDragStart = (_, info) => {
    setIsDragging(true);
    setDragStart({ x: info.point.x, y: info.point.y });
  };

  const handleDragEnd = (_, info) => {
    setIsDragging(false);
    if (info.offset.x > 100) {
      onSwipe("like");
    } else if (info.offset.x < -100) {
      onSwipe("pass");
    }
  };

  const handleTap = (e) => {
    // Only navigate if we didn't drag
    if (!isDragging) {
      e.stopPropagation();
      onViewProfile(profile);
    }
  };

  return (
    <motion.div
      className="swipe-card"
      style={{ 
        x, 
        rotate, 
        zIndex: isTop ? 10 : 1,
        // Hide non-top cards completely to prevent visual overlap
        visibility: isTop ? 'visible' : 'hidden',
        pointerEvents: isTop ? 'auto' : 'none'
      }}
      drag={isTop ? "x" : false}
      dragConstraints={{ left: 0, right: 0 }}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onTap={handleTap}
      initial={{ scale: 1, y: 0, opacity: 1 }}
      animate={{ scale: 1, y: 0, opacity: isTop ? 1 : 0 }}
      exit={{ 
        x: 300, 
        opacity: 0, 
        scale: 0.9,
        transition: { duration: 0.25, ease: "easeOut" } 
      }}
      transition={{ type: "spring", stiffness: 300, damping: 25 }}
      data-testid={`profile-card-${profile.user_id}`}
    >
      {/* Background fallback */}
      <div className="absolute inset-0 bg-gradient-to-b from-gray-700 to-gray-900" />
      
      {/* Photo - Full quality image */}
      <img 
        src={profile.main_photo || profile.picture || profile.photos?.[0] || '/default-avatar.png'}
        alt={profile.name}
        className="absolute inset-0 w-full h-full object-cover cursor-pointer"
        loading="eager"
        onError={(e) => {
          e.target.onerror = null;
          e.target.src = '/default-avatar.png';
        }}
        data-testid="photo-click-area"
      />
      
      {/* Gradient Overlay */}
      <div className="absolute inset-0 profile-gradient" />
      
      {/* Top Row - Left: Buttons + Online Badge | Right: Verified Badge */}
      <div className="absolute top-3 left-3 right-3 flex justify-between items-start">
        {/* Left side - Quick Action Buttons + Online Badge */}
        <div className="flex items-center gap-2">
          {/* Favorite Button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              onFavorite(profile);
            }}
            className="w-8 h-8 rounded-full bg-black/60 backdrop-blur-sm flex items-center justify-center text-yellow-400 hover:bg-yellow-500/30 active:scale-95 transition-all border border-yellow-400/40 touch-target"
            title={tx("Ajouter aux favoris")}
          >
            <Heart className="w-4 h-4" />
          </button>
          {/* Super Like Button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              onSuperLike();
            }}
            className="w-8 h-8 rounded-full bg-black/60 backdrop-blur-sm flex items-center justify-center text-blue-400 hover:bg-blue-500/30 active:scale-95 transition-all border border-blue-400/40 touch-target"
            title="Super Like"
          >
            <Star className="w-4 h-4 fill-current" />
          </button>
          {/* Online Badge or Photo Count */}
          {profile.is_online ? (
            <div className="flex items-center gap-1.5 px-2.5 py-1 bg-green-500/90 rounded-full">
              <div className="w-2 h-2 bg-white rounded-full animate-pulse" />
              <span className="text-xs font-medium text-white">{tx("En ligne")}</span>
            </div>
          ) : profile.photos && profile.photos.length > 1 ? (
            <div className="flex items-center gap-1 px-2 py-1 bg-black/50 backdrop-blur-sm rounded-full">
              <span className="text-xs font-medium text-white">{profile.photos.length} photos</span>
            </div>
          ) : null}
        </div>
        
        {/* Right side - Verified Badge */}
        {profile.is_verified && (
          <div className="flex items-center gap-1 px-2.5 py-1 bg-green-500/90 rounded-full">
            <BadgeCheck className="w-3.5 h-3.5 text-white" />
            <span className="text-xs font-medium text-white">{language === "en" ? "Verified" : "Vérifié"}</span>
          </div>
        )}
      </div>
      
      {/* Like/Nope Stamps */}
      <motion.div 
        className="like-stamp"
        style={{ opacity: likeOpacity }}
      >
        LIKE
      </motion.div>
      <motion.div 
        className="nope-stamp"
        style={{ opacity: nopeOpacity }}
      >
        NOPE
      </motion.div>
      
      {/* Profile Info - Responsive */}
      <div className="absolute bottom-0 left-0 right-0 p-3 sm:p-5 text-white">
        <div className="flex items-center gap-2 mb-1">
          <h2 className="font-heading text-xl sm:text-2xl font-bold">
            {profile.name}, {profile.age || 25}
          </h2>
        </div>
        
        {profile.location && (
          <div className="flex items-center gap-1 text-white/80 mb-2 text-xs sm:text-sm">
            <MapPin className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
            <span className="truncate max-w-[150px] sm:max-w-none">{profile.location}</span>
            {profile.distance && (
              <span className="text-white/60 flex-shrink-0">• {profile.distance} km</span>
            )}
          </div>
        )}
        
        {profile.bio && (
          <p className="text-white/90 line-clamp-2 mb-2 text-xs sm:text-sm">{profile.bio}</p>
        )}
        
        {profile.interests && profile.interests.length > 0 && (
          <div className="flex flex-wrap gap-1 sm:gap-1.5">
            {profile.interests.slice(0, 3).map((interest, i) => (
              <span 
                key={i}
                className="px-1.5 sm:px-2 py-0.5 bg-white/20 backdrop-blur-sm rounded-full text-[10px] sm:text-xs"
              >
                {interest}
              </span>
            ))}
          </div>
        )}

        {/* Message Button on Card */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onMessage();
          }}
          className="mt-3 w-full py-2.5 rounded-xl bg-blue-500/80 backdrop-blur-sm text-white font-medium flex items-center justify-center gap-2 hover:bg-blue-500 transition-all"
          data-testid="card-message-btn"
        >
          <MessageCircle className="w-4 h-4" />
          {language === "en" ? "Send a message" : "Envoyer un message"} 
          <span className="text-xs bg-white/20 px-2 py-0.5 rounded-full">
            {messageStatus.is_premium ? (language === "en" ? "unlimited" : "illimité") : messageStatus.free_messages_remaining > 0 
              ? (language === "en" ? `${messageStatus.free_messages_remaining} free` : `${messageStatus.free_messages_remaining} gratuit${messageStatus.free_messages_remaining > 1 ? 's' : ''}`) 
              : (language === "en" ? '2 credits' : '2 crédits')}
          </span>
        </button>
      </div>
    </motion.div>
  );
}
